import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Copy, KeyRound, Mail, RotateCcw, ShieldOff, SlidersHorizontal, X } from 'lucide-react';
import { Can } from '@casl/react';
import { useAbility } from '@/providers/';
import { Container } from '@/components/container';
import { ConfirmationModal } from '@/components/modal';
import { FileBrowser } from '@/components/files';
import { countTreeFiles } from '@/components/files/tree';
import sharedStorageService from '@/services/shared-storage/sharedStorageService';
import { getApiErrorMessage, toastApiValidationErrors } from '@/utils/ValidationErrors';

const TABS = ['Files', 'Recipients', 'Access Log'];

// Per-recipient access (OL-166). The server enforces View Only; this is the wording.
const ACCESS_OPTIONS = [
  {
    value: 'download',
    label: 'View & Download',
    hint: 'They can open files here and download them.',
  },
  {
    value: 'view',
    label: 'View Only',
    hint: 'They can watch videos and read PDFs and images here, but can’t download anything. Other file types can’t be opened.',
  },
];

const accessLabel = (level) => (ACCESS_OPTIONS.find((option) => option.value === level) ?? ACCESS_OPTIONS[0]).label;

const recipientSchema = Yup.object({
  name: Yup.string().trim().required('Enter their name.').max(255),
  email: Yup.string().trim().email('Enter a valid email address.').required('Enter their email.'),
  expires_at: Yup.string().nullable(),
  access_level: Yup.string().oneOf(ACCESS_OPTIONS.map((option) => option.value)),
  message: Yup.string().max(2000, 'Keep the message under 2,000 characters.'),
});

/** Email the link, with an optional message (OL-174). Never the PIN. */
const InviteFields = () => {
  const { values } = useFormikContext();

  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-2 text-2sm cursor-pointer">
        <Field type="checkbox" name="send_invite" className="checkbox checkbox-sm" />
        <span className="font-medium text-gray-900">Email them the link</span>
      </label>
      {values.send_invite && (
        <div>
          <label className="form-label">Message (optional)</label>
          <Field as="textarea" name="message" rows={3} className="textarea w-full" />
          <ErrorMessage name="message" component="p" className="text-danger text-2xs mt-1" />
          <p className="text-2xs text-gray-500 mt-1">
            Sent with the link. Don’t put the PIN here: you’ll give them that by phone or text.
          </p>
        </div>
      )}
    </div>
  );
};

/** The InviteFields part of an add or restore request. */
const invitePayload = (values) => ({
  send_invite: values.send_invite,
  message: values.send_invite ? values.message : null,
});

/** Turns the add response into what the PIN window shows. */
const credentialsFrom = (data, values) => ({
  name: data.recipient.name,
  email: data.recipient.email,
  pin: data.pin,
  link: data.link,
  emailRequested: !!values.send_invite,
  invited: !!data.invited,
});

/** The Access radio pair, shared by the Add Recipient and Change access windows. */
const AccessLevelField = () => (
  <div>
    <label className="form-label">Access</label>
    <div className="flex flex-col gap-2 mt-1">
      {ACCESS_OPTIONS.map((option) => (
        <label key={option.value} className="flex items-start gap-2 text-2sm cursor-pointer">
          <Field type="radio" name="access_level" value={option.value} className="radio radio-sm mt-0.5 shrink-0" />
          <span>
            <span className="font-medium text-gray-900">{option.label}</span>
            <span className="block text-2xs text-gray-500">{option.hint}</span>
          </span>
        </label>
      ))}
    </div>
  </div>
);

/** The recipient's own end date, shared by the Add Recipient and Change access windows. */
const ExpiresAtField = ({ hint }) => (
  <div>
    <label className="form-label">Their access ends on</label>
    <Field type="date" name="expires_at" className="input w-full" />
    <ErrorMessage name="expires_at" component="p" className="text-danger text-2xs mt-1" />
    <p className="text-2xs text-gray-500 mt-1">{hint}</p>
  </div>
);

ExpiresAtField.propTypes = {
  hint: PropTypes.string.isRequired,
};

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '—';

const ACTION_LABELS = {
  space_created: 'Space created',
  space_revoked: 'Access revoked',
  recipient_added: 'Recipient added',
  recipient_revoked: 'Recipient revoked',
  recipient_updated: 'Access changed',
  recipient_restored: 'Access restored',
  pin_regenerated: 'PIN regenerated',
  invite_sent: 'Invite sent',
  unlock_attempt: 'Incorrect PIN',
  unlock_locked: 'Locked out',
  space_opened: 'Opened the space',
  file_previewed: 'Previewed a file',
  file_downloaded: 'Downloaded a file',
  folder_downloaded: 'Downloaded a folder (.zip)',
  access_denied: 'Access denied',
};

/**
 * One shared space: its documents, who can reach them, and the access trail.
 */
const SharedStorageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ability } = useAbility();

  const [tab, setTab] = useState('Files');
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [credentials, setCredentials] = useState(null); // { name, pin, link } — shown once
  const [pendingRevoke, setPendingRevoke] = useState(null);
  const [editingAccess, setEditingAccess] = useState(null); // the recipient whose access is being changed
  const [restoring, setRestoring] = useState(null); // the revoked recipient being given access again
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await sharedStorageService.getSpace(id);
      setSpace(data);
    } catch {
      toast.error('Could not load this space.', { description: 'It may have been deleted.' });
      navigate('/shared-storage');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const { data } = await sharedStorageService.getAccessLog(id);
      setLogs(data?.data ?? []);
    } catch {
      toast.error('Could not load the access history.');
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (tab === 'Access Log') loadLogs();
  }, [tab, loadLogs]);

  const handleAddRecipient = async (values, { setSubmitting, setErrors, resetForm }) => {
    try {
      const { status, data } = await sharedStorageService.addRecipient(id, {
        name: values.name,
        email: values.email,
        expires_at: values.expires_at || null,
        access_level: values.access_level,
        ...invitePayload(values),
      });
      if (status) {
        setShowAdd(false);
        resetForm();
        // The PIN is hashed server-side — this response is the only place it
        // will ever appear.
        setCredentials(credentialsFrom(data, values));
        load();
      }
    } catch (error) {
      if (toastApiValidationErrors(error, { setErrors })) return;
      toast.error('Could not add that recipient.', {
        description: getApiErrorMessage(error, 'Please try again in a moment.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvite = async (recipient) => {
    try {
      await sharedStorageService.sendInvite(id, recipient.id);
      toast.success('Invite sent.', { description: `${recipient.email} has the link. Give them the PIN separately.` });
      load();
    } catch {
      toast.error('Could not send the invite.', { description: 'Please try again in a moment.' });
    }
  };

  const handleRegeneratePin = async (recipient) => {
    try {
      const { data } = await sharedStorageService.regeneratePin(id, recipient.id);
      setCredentials({ name: recipient.name, pin: data.pin, link: null });
      load();
    } catch {
      toast.error('Could not generate a new PIN.', { description: 'Please try again in a moment.' });
    }
  };

  const handleChangeAccess = async (values, { setSubmitting, setErrors }) => {
    const recipient = editingAccess;
    try {
      await sharedStorageService.updateRecipient(id, recipient.id, {
        access_level: values.access_level,
        expires_at: values.expires_at || null,
      });
      setEditingAccess(null);
      toast.success('Access updated.', {
        description: `${recipient.name} now has ${accessLabel(values.access_level)} access. This applies from their next click.`,
      });
      load();
    } catch (error) {
      if (toastApiValidationErrors(error, { setErrors })) return;
      toast.error('Could not change their access.', {
        description: getApiErrorMessage(error, 'Please try again in a moment.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestore = async (values, { setSubmitting, setErrors }) => {
    const recipient = restoring;
    try {
      const { status, data } = await sharedStorageService.restoreRecipient(id, recipient.id, {
        access_level: values.access_level,
        expires_at: values.expires_at || null,
        ...invitePayload(values),
      });
      if (status) {
        setRestoring(null);
        setCredentials(credentialsFrom(data, values));
        load();
      }
    } catch (error) {
      if (toastApiValidationErrors(error, { setErrors })) return;
      toast.error('Could not restore their access.', {
        description: getApiErrorMessage(error, 'Please try again in a moment.'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRevoke = async () => {
    const recipient = pendingRevoke;
    setPendingRevoke(null);
    try {
      await sharedStorageService.revokeRecipient(id, recipient.id);
      toast.success('Recipient revoked.', { description: `${recipient.name} can no longer open the space.` });
      load();
    } catch {
      toast.error('Could not revoke that recipient.', { description: 'Please try again in a moment.' });
    }
  };

  const copy = (text, what) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${what} copied.`),
      () => toast.error(`Could not copy the ${what.toLowerCase()}.`),
    );
  };

  const recipientState = (r) => {
    if (r.revoked_at) return { label: 'Revoked', badge: 'badge-danger' };
    const expiry = r.expires_at ?? space?.expires_at;
    if (expiry && new Date(expiry) < new Date()) return { label: 'Expired', badge: 'badge-warning' };
    if (!r.invited_at) return { label: 'Not invited', badge: 'badge-secondary' };
    return { label: 'Active', badge: 'badge-success' };
  };

  if (loading) {
    return (
      <Container>
        <p className="text-center text-2sm text-gray-600 py-10">Loading…</p>
      </Container>
    );
  }

  const expired = space?.expires_at && new Date(space.expires_at) < new Date();

  return (
    <Container>
      <div className="card min-w-full mb-4">
        <div className="card-header flex-wrap gap-2">
          <div>
            <h3 className="card-title">{space?.name}</h3>
            {space?.description && <p className="text-2sm text-gray-600 mt-1">{space.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            {space?.documents_count != null && (
              <span className="badge badge-pill badge-outline" title="Documents in this space">
                {space.documents_count} {space.documents_count === 1 ? 'document' : 'documents'}
              </span>
            )}
            {space?.status === 'revoked' && <span className="badge badge-pill badge-outline badge-danger">Revoked</span>}
            {expired && space?.status !== 'revoked' && (
              <span className="badge badge-pill badge-outline badge-warning">Expired</span>
            )}
            {space?.expires_at && (
              <span className="text-2xs text-gray-500">Access ends {fmtDateTime(space.expires_at)}</span>
            )}
          </div>
        </div>

        <div className="card-body">
          <div className="flex items-center gap-1 border-b border-gray-200 mb-4">
            {TABS.map((name) => (
              <button
                key={name}
                onClick={() => setTab(name)}
                className={`px-4 py-2 text-2sm border-b-2 -mb-px ${
                  tab === name
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {name}
              </button>
            ))}
          </div>

          {/* Mirrors the server: uploading into a space needs manage-shared-storage
              ('handle' after the CASL rename), not just access. */}
          {tab === 'Files' && (
            <FileBrowser
              fileableType="shared_space"
              fileableId={id}
              readOnly={!ability.can('handle', 'shared-storage')}
              onTreeLoaded={(nodes) =>
                // Keep the header badge live: uploads/deletes in this tab
                // otherwise leave the mount-time count stale.
                setSpace((current) =>
                  current ? { ...current, documents_count: countTreeFiles(nodes) } : current,
                )
              }
            />
          )}

          {tab === 'Recipients' && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <p className="text-2sm text-gray-600">
                  Each person gets their own link and PIN. Send the PIN by phone, not email.
                </p>
                <Can I="handle" a="shared-storage" ability={ability}>
                  <button className="btn btn-sm btn-primary" onClick={() => setShowAdd(true)}>
                    Add Recipient
                  </button>
                </Can>
              </div>

              {(space?.recipients ?? []).length === 0 ? (
                <p className="text-center text-2sm text-gray-500 py-10">
                  Nobody has been invited yet.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table align-middle text-2sm w-full">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Access</th>
                        <th>Status</th>
                        <th>Last opened</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {space.recipients.map((r) => {
                        const state = recipientState(r);
                        return (
                          <tr key={r.id}>
                            <td>{r.name}</td>
                            <td className="text-gray-600">{r.email}</td>
                            <td className="text-gray-600 whitespace-nowrap">{accessLabel(r.access_level)}</td>
                            <td>
                              <span className={`badge badge-sm badge-pill badge-outline ${state.badge}`}>
                                {state.label}
                              </span>
                            </td>
                            <td className="text-gray-600">{fmtDateTime(r.last_accessed_at)}</td>
                            <td>
                              <Can I="handle" a="shared-storage" ability={ability}>
                                {r.revoked_at ? (
                                  <div className="flex items-center justify-end">
                                    <button
                                      className="btn btn-xs btn-light"
                                      title="Give them access again, with a new link and PIN"
                                      onClick={() => setRestoring(r)}
                                    >
                                      <RotateCcw size={13} /> Restore access
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-end gap-1">
                                    <button
                                      className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
                                      title="Change access"
                                      onClick={() => setEditingAccess(r)}
                                    >
                                      <SlidersHorizontal size={15} />
                                    </button>
                                    <button
                                      className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
                                      title={r.invited_at ? 'Resend invite' : 'Send invite'}
                                      onClick={() => handleInvite(r)}
                                    >
                                      <Mail size={15} />
                                    </button>
                                    <button
                                      className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-primary"
                                      title="Generate a new PIN"
                                      onClick={() => handleRegeneratePin(r)}
                                    >
                                      <KeyRound size={15} />
                                    </button>
                                    <button
                                      className="btn btn-icon btn-xs btn-clear text-gray-500 hover:text-danger"
                                      title="Revoke access"
                                      onClick={() => setPendingRevoke(r)}
                                    >
                                      <ShieldOff size={15} />
                                    </button>
                                  </div>
                                )}
                              </Can>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'Access Log' && (
            <div>
              <p className="text-2sm text-gray-600 mb-3">
                Every open, preview and download is recorded here. This record is kept and cannot be edited.
              </p>
              {logsLoading ? (
                <p className="text-center text-2sm text-gray-600 py-10">Loading…</p>
              ) : logs.length === 0 ? (
                <p className="text-center text-2sm text-gray-500 py-10">Nothing recorded yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="table align-middle text-2sm w-full">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Who</th>
                        <th>What</th>
                        <th>File</th>
                        <th>IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td className="whitespace-nowrap text-gray-600">{fmtDateTime(log.created_at)}</td>
                          <td>{log.actor_label ?? '—'}</td>
                          <td>
                            <span
                              className={
                                log.outcome === 'denied' ? 'text-danger' : 'text-gray-900'
                              }
                            >
                              {ACTION_LABELS[log.action] ?? log.action}
                            </span>
                          </td>
                          <td className="text-gray-600">{log.target_label ?? '—'}</td>
                          <td className="text-gray-500 tabular-nums">{log.ip ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* add recipient */}
      {showAdd && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-body modal-animate-scale w-4/5 max-w-lg">
            <button type="button" onClick={() => setShowAdd(false)} className="modal-close-btn">
              <X size={16} />
            </button>
            <h2 className="text-lg font-medium text-gray-900 my-2">Add Recipient</h2>

            <Formik
              initialValues={{ name: '', email: '', expires_at: '', access_level: 'download', send_invite: true, message: '' }}
              validationSchema={recipientSchema}
              onSubmit={handleAddRecipient}
            >
              {({ isSubmitting }) => (
                <Form className="flex flex-col gap-3 mt-2">
                  <div>
                    <label className="form-label">Name</label>
                    <Field name="name" className="input w-full" />
                    <ErrorMessage name="name" component="p" className="text-danger text-2xs mt-1" />
                  </div>
                  <div>
                    <label className="form-label">Email</label>
                    <Field name="email" type="email" className="input w-full" />
                    <ErrorMessage name="email" component="p" className="text-danger text-2xs mt-1" />
                  </div>
                  <ExpiresAtField hint="Optional. This can only shorten their access, never extend it past the space’s own date." />
                  <AccessLevelField />
                  <InviteFields />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Adding…' : 'Add'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      {/* one-time PIN reveal */}
      {credentials && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-body modal-animate-scale w-4/5 max-w-lg">
            <h2 className="text-lg font-medium text-gray-900 my-2">
              PIN for {credentials.name}
            </h2>

            <div className="bg-warning-light border border-warning rounded-md p-3 text-2sm text-gray-800 mb-3">
              This is the only time this PIN will be shown. Write it down now. Give it to
              them by phone or in person — <span className="font-medium">never by email</span>,
              or the link and the PIN end up in the same inbox.
            </div>

            <label className="form-label">PIN</label>
            <div className="flex items-center gap-2 mb-3">
              <code className="input w-full font-mono text-lg tracking-widest">{credentials.pin}</code>
              <button className="btn btn-icon btn-light" title="Copy PIN" onClick={() => copy(credentials.pin, 'PIN')}>
                <Copy size={15} />
              </button>
            </div>

            {credentials.link && (
              <>
                <label className="form-label">Their link</label>
                <div className="flex items-center gap-2">
                  <input readOnly value={credentials.link} className="input w-full text-2xs" />
                  <button
                    className="btn btn-icon btn-light"
                    title="Copy link"
                    onClick={() => copy(credentials.link, 'Link')}
                  >
                    <Copy size={15} />
                  </button>
                </div>
                {credentials.invited ? (
                  <p className="text-2xs text-success mt-1">
                    This link is being emailed to {credentials.email}, without the PIN.
                  </p>
                ) : credentials.emailRequested ? (
                  <p className="text-2xs text-danger mt-1">
                    We couldn’t email the link. Copy it and send it to them, or try Send invite on their row.
                  </p>
                ) : (
                  <p className="text-2xs text-gray-500 mt-1">
                    Copy it and send it to them, or use Send invite on their row to email it.
                  </p>
                )}
              </>
            )}

            <div className="flex items-center justify-end mt-4">
              <button className="btn btn-primary" onClick={() => setCredentials(null)}>
                I have written it down
              </button>
            </div>
          </div>
        </div>
      )}

      {/* change access (OL-167) */}
      {editingAccess && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-body modal-animate-scale w-4/5 max-w-lg">
            <button type="button" onClick={() => setEditingAccess(null)} className="modal-close-btn">
              <X size={16} />
            </button>
            <h2 className="text-lg font-medium text-gray-900 my-2">Change access for {editingAccess.name}</h2>
            <p className="text-2sm text-gray-600">
              Their link and PIN stay the same. The change applies from their next click.
            </p>

            <Formik
              initialValues={{
                access_level: editingAccess.access_level ?? 'download',
                expires_at: editingAccess.expires_at ? editingAccess.expires_at.slice(0, 10) : '',
              }}
              onSubmit={handleChangeAccess}
            >
              {({ isSubmitting }) => (
                <Form className="flex flex-col gap-3 mt-3">
                  <AccessLevelField />
                  <ExpiresAtField hint="Leave empty for no date of their own. The space’s own date still applies." />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button type="button" className="btn btn-outline" onClick={() => setEditingAccess(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      {/* restore a revoked recipient (OL-175) */}
      {restoring && (
        <div className="modal-wrapper modal-animate-fade">
          <div className="modal-body modal-animate-scale w-4/5 max-w-lg">
            <button type="button" onClick={() => setRestoring(null)} className="modal-close-btn">
              <X size={16} />
            </button>
            <h2 className="text-lg font-medium text-gray-900 my-2">Restore access for {restoring.name}</h2>
            <p className="text-2sm text-gray-600">
              They get a new link and PIN. The old ones stay switched off.
            </p>

            <Formik
              initialValues={{
                access_level: restoring.access_level ?? 'download',
                // A date that has already passed would be refused, so it starts empty.
                expires_at:
                  restoring.expires_at && new Date(restoring.expires_at) > new Date()
                    ? restoring.expires_at.slice(0, 10)
                    : '',
                send_invite: true,
                message: '',
              }}
              validationSchema={recipientSchema.pick(['access_level', 'message'])}
              onSubmit={handleRestore}
            >
              {({ isSubmitting }) => (
                <Form className="flex flex-col gap-3 mt-3">
                  <AccessLevelField />
                  <ExpiresAtField hint="Leave empty for no date of their own. The space’s own date still applies." />
                  <InviteFields />
                  <div className="flex items-center justify-end gap-2 mt-2">
                    <button type="button" className="btn btn-outline" onClick={() => setRestoring(null)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Restoring…' : 'Restore access'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      <ConfirmationModal
        show={!!pendingRevoke}
        onClose={() => setPendingRevoke(null)}
        onConfirm={confirmRevoke}
        title="Revoke this recipient?"
        btnText="Revoke"
        message={`${pendingRevoke?.name ?? 'They'} will lose access immediately, including any session they have open right now. You can restore their access later; they would get a new link and PIN.`}
      />
    </Container>
  );
};

export { SharedStorageDetail };
